import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../components/auth/AuthProvider';
import { deleteBlogPost, getAdminBlogPosts, toggleBlogPostPublish } from '../../lib/api';

const BlogPosts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchPosts = useCallback(async () => {
    try {
      const data = await getAdminBlogPosts();
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error(error.message || 'Blog yazıları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchPosts();
  }, [user, navigate, fetchPosts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteBlogPost(id);
      setPosts((prev) => prev.filter((post) => post.id !== id));
      toast.success('Blog yazısı başarıyla silindi');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error(error.message || 'Blog yazısı silinirken bir hata oluştu');
    }
  };

  const handlePublish = async (id) => {
    try {
      const updated = await toggleBlogPostPublish(id);
      setPosts((prev) => prev.map((post) => (post.id === id ? updated : post)));
      toast.success(`Blog yazısı ${updated.is_published ? 'yayınlandı' : 'taslağa alındı'}`);
    } catch (error) {
      console.error('Error updating blog post status:', error);
      toast.error(error.message || 'Blog yazısı durumu güncellenirken bir hata oluştu');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">Lütfen giriş yapın</h2>
          <p className="mt-2 text-gray-600">Blog yazılarını görüntülemek için giriş yapmalısınız.</p>
          <Link
            to="/admin/login"
            className="mt-4 inline-block px-4 py-2 bg-[#dbc582] text-white rounded hover:bg-[#c5b274]"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#dbc582]"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-900">Blog Yazıları</h1>
          <Link
            to="/admin/blogs/new"
            className="bg-[#dbc582] text-white px-4 py-2 rounded-md hover:bg-[#c5b274]"
          >
            Yeni Blog Yazısı
          </Link>
        </div>

        {posts.length === 0 ? (
          <div className="mt-8 text-center">
            <h3 className="text-lg font-medium text-gray-900">Henüz blog yazısı yok</h3>
            <p className="mt-1 text-gray-500">
              İlk blog yazınızı oluşturmak için "Yeni Blog Yazısı" butonuna tıklayın.
            </p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Başlık</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Durum</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Oluşturulma</th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">İşlemler</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {posts.map((post) => (
                        <tr key={post.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {post.image_url && (
                                <div className="h-10 w-10 flex-shrink-0">
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={post.image_url}
                                    alt=""
                                  />
                                </div>
                              )}
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">{post.title}</div>
                                <div className="text-gray-500">{post.excerpt}</div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span
                              className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                post.is_published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {post.is_published ? 'Yayında' : 'Taslak'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(post.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handlePublish(post.id)}
                                className="text-[#dbc582] hover:text-[#c5b274]"
                              >
                                {post.is_published ? 'Taslağa Al' : 'Yayınla'}
                              </button>
                              <Link
                                to={`/admin/blogs/${post.id}/edit`}
                                className="text-[#dbc582] hover:text-[#c5b274]"
                              >
                                Düzenle
                              </Link>
                              <button
                                onClick={() => handleDelete(post.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPosts;
